import React from 'react';
import CgPopover from 'modules/Common/ui/CgPopover';
import MethodologyContent from './MethodologyContent';
import cgLogo from 'assets/icons/logo.png';

// "Powered by Carbon Glance" attribution badge for partner iframe embeds.
//
// Layout:
//   ┌─────────────┐
//   │  POWERED BY │   ← small uppercase label, tracked to match logo width
//   │ [CG LOGO]   │   ← logo already contains the "Carbon Glance" wordmark
//   └─────────────┘
//
// Width-matching trick: the container has a fixed width (BADGE_WIDTH_PX) and
// the logo fills it via w-full + h-auto so its aspect ratio is preserved.
// The POWERED BY label is centered at that same width. Adjust BADGE_WIDTH_PX
// alone to scale the whole badge.
//
// Interactions:
//   - Hover the badge → CgPopover with methodology / key assumptions
//   - Click the badge → opens carbonglance.com in a new tab (UTM-tagged)
//
// Both behaviours coexist: CgPopover attaches mouse events to the cloned
// trigger but leaves the underlying <a href> intact.

const BADGE_WIDTH_PX = 200;

// `variant` controls what's shown in the hover popover:
//   - "methodology" (default): full key-assumptions breakdown — used by the
//     cost-radar embed where the numbers themselves are the product.
//   - "cta": a single-line invitation to build a diagram on Carbon Glance —
//     used by the public shared-diagram page, where methodology details
//     would be redundant (the diagram is the methodology).
const PoweredByCarbonGlance = ({ utmMedium = 'cost-radar', variant = 'methodology' }) => {
  const href = `https://carbonglance.com?utm_source=embed&utm_medium=${encodeURIComponent(utmMedium)}`;
  const isCta = variant === 'cta';
  return (
    <CgPopover
      trigger="hover"
      position="top"
      sideOffset={8}
      openDelay={150}
      closeDelay={200}
      popoverTitle={isCta ? null : 'Key calculation assumptions'}
      popoverContentClassName={isCta ? 'max-w-[280px]' : 'max-w-[400px]'}
      triggerElement={
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          title="Visit Carbon Glance"
          data-testid="powered-by-carbon-glance"
          className="flex flex-col items-center gap-2 px-4 py-2.5 bg-white/95 backdrop-blur-sm rounded-xl border border-gray-200 shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all duration-200"
          style={{ width: `${BADGE_WIDTH_PX}px` }}
        >
          <span className="text-xs uppercase tracking-[0.22em] font-semibold text-gray-500 leading-none text-center">
            Powered by
          </span>
          <img
            src={cgLogo}
            alt="Carbon Glance"
            className="block w-full h-auto object-contain"
            draggable={false}
          />
        </a>
      }
      popoverContent={
        isCta ? (
          <p className="text-sm text-gray-700 m-0 text-center">
            Create your own factory schematic diagram on Carbon Glance
          </p>
        ) : (
          <MethodologyContent />
        )
      }
    />
  );
};

export default PoweredByCarbonGlance;
